import { UntypedFormControl } from '@angular/forms';
import { SafeResourceUrl } from '@angular/platform-browser';
import { AnswerStatus } from '../inspection/answer.status';
import { InspectionStatus } from '../inspection/inspection.status';
import { Feature } from './features';

export interface Organization {
  id?: number;
  active?: boolean;
  name: string;
  users?: User[];
  features?: Feature[];
}

export interface Organization {
  id?: number;
  active?: boolean;
  name: string;
  users?: User[];
}

export interface OnboardingItem {
  id?: number;
  order: number;
  header: string;
  body: string;
  image: string;
  active: boolean;
}

export interface Broker {
  id?: number;
  name: string;
  phone: string;
  email: string;
  active: boolean;
}

export interface ProductType {
  id?: number;
  name: string;
  active: boolean;
  regex: string;
  templateId: string;
  emailSender: string;
  emailReplyTo: string;
}

export interface Customer {
  id?: number;
  name: string;
}

export interface CustomerList extends Customer {
  policyCount: number;
  inspectionCount: number;
}

export interface Contact {
  id?: number;
  name: string;
  phone: string;
  email: string;
}

export interface Policy {
  id?: number;
  active: boolean;
  broker?: Broker;
  brokerId: number;
  customer: Customer;
  number: string;
  productType: ProductType;
  effectiveDate: Date;
  label?: string;
  group?: PolicyGroup;
}

export interface PolicyList extends Policy {
  inspectionCount: number;
}

export interface User {
  id?: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  type?: UserTypes;
  roles?: Role[];
  googleAuth?: boolean;
  organization?: Organization;
}

export interface Role {
  id?: number;
  name: string;
  description: string;
  permissions?: any[];
  checked?: boolean; // only ui
}

export interface Vendor {
  id?: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
}

export interface Setting {
  id?: number;
  key: string;
  value: string;
  type: SettingType;
  description?: string;
  readOnly: boolean;
  visibleToInspector: boolean;
}

export enum SettingType {
  STRING = 'STRING',
  MULTILINE_STRING = 'MULTILINE_STRING',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  JSON = 'JSON',
}

export interface Help {
  id?: number;
  title: string;
  content: string;
  section: HelpSection;
  order: number;
}

export interface HelpCategory {
  id?: number;
  name: string;
}

export interface HelpSection {
  id?: number;
  name: string;
  category?: any;
}

export interface Inspector {
  id?: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  vendor?: Vendor;
  active?: boolean;
  productTypes?: { id: number; name: string }[];
  coordinates?: [number, number];
  productTypesConcat?: string;
}

export interface Section {
  id?: number;
  name: string;
  version?;
  order?;
  item?: InspectionItem;
  inspectionItems?;
  questions?: Question[];
  animationData?: string;
}

export interface Answer {
  id?: number;
  answer: string;
  buildingIndex?: number;
  questionId: number;
  question?: any;
  inspectionId?: number;
  flags?: Flag[];
  comments?: any[];
  pictures?: any[];
  status?: AnswerStatus;
  changes?: any[];
  flaggingScore?: number;
  location?: [number, number];
  gps?: [number, number];
}
export interface AnswerChange {
  id?: number;
  date: Date;
  username: string;
  user: User;
  answer: Answer;
  log: { type: string; [key: string]: any; isBulk: true };
}

export interface InspectionItem {
  id?: number;
  name: string;
  minInstances?: number;
  maxInstances?: number;
  order?: number;
  reviewGrouped?: boolean;
  sections?: Section[];
}

export interface InspectionVersion {
  id?: number;
  name?: string;
  active?: boolean;
  items?: InspectionItem[];
  inspectionCount?: number;
  isCustom?: boolean;
  isValid?: boolean;
  invalidQuestionsCount?: number;
  tags?: string;
}

export interface InvalidQuestion {
  originalId: number;
  sectionId: number;
  questionText: string;
  type: string;
  requiredId: number;
}

export interface HelpPicture {
  Location: string;
  caption: string;
}

export interface QuestionOption {
  text: string;
  helpText?: string;
  helpPictures?: HelpPicture[];
  flaggingScore?: number;
  flaggingComment?: string;
}

export interface Question {
  id?: number;
  version?;
  active?: boolean;
  text?: string;
  type?: string;
  options?: QuestionOption[];
  legacyId?: string;
  adminVisibility?: any;
  adminVisibilityAnswer?: any;
  adminVisibilityQuestion?: any;
  adminVisibilityQuestionId?: any;
  inspectorVisibility?: string;
  inspectorShowIf?: string;
  inspectorResponseIs?: string;
  inspectorResponseRequiredIf?: string;
  pictureRequired?: string;
  pictureIf?: string;
  pictureOrientation?: string;
  pictureGuidanceText?: string;
  notes?: string;
  notesRequired?: string;
  notesIf?: string;
  adminResponsability?: string;
  adminResponseIs?: string;
  adminResponseIf?: string;
  pictureToReview?: string;
  section?: Section;
  order?: number;
  helpText?: string;
  helpPictures?: HelpPicture[];
  inspectorVisibilityQuestionId?;
  inspectorVisibilityQuestion?;
  inspectorVisibilityAnswer?;
  pictureRequiredQuestionId?;
  pictureRequiredQuestion?;
  pictureRequiredAnswer?;
  notesRequiredQuestionId?;
  notesRequiredQuestion?;
  notesRequiredAnswer?;
  locationRequired?;
  locationRequiredQuestionId?;
  locationRequiredQuestion?;
  locationRequiredAnswer?;
  answer?;
  repeat?;
  related?: Question[];
  showRelated?: boolean;
  mandatoryReview?: boolean;
}

export interface Inspection {
  id?: number;
  archived?: boolean;
  policy?: Policy;
  purpose?: InspectionPurpose;
  policyNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  inspector?: Inspector;
  inspectorId?: number;
  vendor?: Vendor;
  vendorId?: number;
  assignee?: User;
  assigneeId?: number;
  policyEffectiveDate?;
  dueDate?;
  forecasted?: boolean;
  status?: InspectionStatus;
  version?;
  versionId?;
  items?: ItemInfo[];
  resubmitted?;
  completed?;
  rejected?;
  paid?;
  accepted?;
  changes?;
  customerName?;
  contactName?;
  contactEmail?;
  contactPhone?;
  validation?: any[];
  nPictures?;
  answers?: any[];
  lastSynced?;
  daysSince?: any;
  hadSyncProblems?: boolean;
  flaggingScore?: number;
  underwritingStatus?: UnderwritingStatus;
  violationRecommendedDeadline?: Date;
  violationActionRequiredDeadline?: Date;
  underwrintingClosed?: Date;
  hash?: string;
  location?: [number, number];
  notes?: string;
  createdAt?: Date;
  locationRelevance?: number;
  boxFileRequestUrl?: string;
  boxFolderId?: string;
  boxFolderUrl?: string;
  boxFolderFullUrl?: SafeResourceUrl;
}

export interface Flag {
  id?: number;
  answerId?;
  subtypeId?;
  text;
  description?: string;
  x?;
  y?;
  pictureId?;
  picture?;
  subtype?: FlagSubtype;
  comments?: Comment[];
  violation?: ViolationInfo;
  discrepancy?: DiscrepancyInfo;
  score?: number;
  group?: FlagGroup;
  groupId?: number;
  order?: number;
  date?: Date;
  updatedAt?: Date;
  ignore?: boolean;
  changes?: any[];
  status?: any;
  resolved?: Date;
  helpPictures?: any[];
}

export interface FlagGroup {
  id?: number;
  name?: string;
  changeSet?: any;
  flags?: Flag[];
  inspectionId?: number;
  inspection?: Inspection;
  subtypeId?;
  subtype?: FlagSubtype;
  description?: string;
  violation?: ViolationInfo;
  discrepancy?: DiscrepancyInfo;
  count?: number;
  helpPictures?: any[];
  excluded?: boolean;
}

export interface ViolationInfo {
  elevatedRisk?: string;
  deadline?: string;
  whyDoesThisMatter?: string;
  actionRequested?: string;
  internalNotes?: string;
  documentationRequested?: string;
  url?: string;
  urlTitle?: string;
  excludeFromReport: boolean;
}

export interface DiscrepancyInfo {
  stated?: string;
  actual?: string;
  resultOfTheFinding?: string;
  impact?: string;
  effectiveDate?: Date;
  commentsForReport?: string;
  url?: string;
}

export interface FlagTemplate {
  id?: number;
  name?: string;
  label?: string;
  subtype?: FlagSubtype;
  subtypeId?: number;
  violation?: ViolationInfo;
  discrepancy?: DiscrepancyInfo;
  subgroupId?: number;
  subgroup?: any;
  description?: string;
  helpPictures?: any[];
}

export interface Note {
  userId: number;
  username: string;
  date: Date;
  text: string;
}

export interface FlagSubtype {
  id?: number;
  type: FlagType;
  typeId?: number;
  name: string;
  label?: string;
  color?: string;
}
export interface FlagType {
  id?: number;
  name: string;
}

export interface ItemInfo {
  id: number;
  quantity: number;
  itemId: number;
  item: InspectionItem;
  inspectionId: number;
  names?: string[];
  namesInputs?: UntypedFormControl[];
}

export interface ListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  [key: string]: any;
}

export type ListResults<T> = [T[], number];

export interface RecLetter {
  data: RecLetterData;
  createdAt: Date;
}
export interface RecLetterData {
  inspection: Inspection;
  subtypeInfo: any[];
  violation1: Flag[];
  violation2: Flag[];
  violation3: Flag[];
  discrepancy: Flag[];
  template?: string;
}

export interface LetterBlock {
  id?: number;
  name: string;
  code: string;
}

export enum UserTypes {
  ADMIN = 'AdminUser',
  VENDOR = 'Vendor',
  INSPECTOR = 'Inspector',
}

export enum ChangelogTypes {
  FLAG = 'FlagChange',
  COMMENT = 'CommentChange',
  EDIT = 'EditChange',
  ACCEPT = 'AcceptChange',
  REJECT = 'RejectChange',
  DELETE = 'DeleteChange',
}

export enum UnderwritingStatus {
  INVALID_STATE,
  PREPARE_FLAGS,
  PREPARATION_IN_PROGRESS,
  LETTER_SENT,
  RESOLUTION_IN_PROGRESS,
  CLOSED,
}

export enum FlagStatus {
  INVALID_STATE,
  UNRESOLVED,
  PARTIAL_RESPONSE,
  RESOLVED,
}

export enum InspectionPurpose {
  NEW = 'New',
  RENEWAL = 'Renewal',
  CLAIM = 'Claim',
  PRE_BINDING = 'Pre-binding',
  UNDERWRITING = 'Underwriting',
  SPECIAL = 'Special',
}

export interface FlagGroupTag {
  id?: number;
  name: string;
  label?: string;
  subgroups?: any[];
}

export interface QuestionTag {
  id?: number;
  name: string;
}

export interface InspectionVersionTag {
  id?: number;
  name: string;
}

export interface SentEmailType {
  RECOMMENDATION_LETTER_EMAIL: string;
}

export interface SentEmail {
  id?: number;
  organizationId?: number;
  type: SentEmailType;
  body: string;
  from: string;
  bcc?: string;
  to: string;
  cc?: string;
  subject: string;
  inspectionId: number;
  inspection: Inspection;
  sentDate: Date;
}

export interface SendgridEvent {
  timestamp: Date;
  event: string;
  sg_event_id: string;
  sg_message_id: string;
  sendGridObject: any;
  message?: SentEmail;
  emailLink?: string;
}
export interface PolicyGroup {
  id?: number;
  name: string;
  policies?: Policy[];
  policyCount?: number;
}

export interface FlagSubGroup {
  name?: string;
  label?: string;
  templates?: FlagTemplate[];
  id?: number;
  groupId?: number;
  organizationId?: number;
}
